import { motion } from 'framer-motion';
import React, { createContext, Fragment, HTMLAttributes, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ScriptService } from '../../controllers/ScriptService';
import { useEventListener } from '../../hooks/use-event-listener';
import { SvgChevronLeft, SvgChevronRight } from '../../icons';
import { classes, warn } from '../../utils/commonUtils';
import { reset, set } from '../../utils/dom';
import { Link, LinkProps } from '../Link/Link';
import { generateSwimlaneKey } from './key';
import { getMovableDirection, scroll } from './scroll';
import css from './Swimlane.module.scss';
import { useTranslation } from 'react-i18next';

interface SwimlaneProps extends HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  className?: string;
}

export type MoveableDirection = 'start' | 'end' | 'both' | 'none';

interface SwimlaneContextValues {
  slider: React.RefObject<HTMLDivElement>;
  movableDirection: MoveableDirection;
  scrollBy: (delta: number) => void;
  scrollTo: (position: number) => void;
}

const SwimlaneContext = createContext<SwimlaneContextValues | null>(null);

function useSwimlaneContext() {
  const context = React.useContext(SwimlaneContext);

  if (context === null) {
    throw new Error('useSwimlaneContext must be used within a Swimlane');
  }

  return context;
}

const cache = new Map<string, number>();

interface SwimlanePartProps {
  children: React.ReactNode;
  className?: string;
}

export function Swimlane({ children, className }: SwimlaneProps) {
  const [movableDirection, setMovableDirection] = useState<MoveableDirection>('end');

  const slider = useRef<HTMLDivElement>(null);
  const key = generateSwimlaneKey(children);

  const handleMovableDirection = useCallback(
    (element: HTMLElement | null) => {
      if (element) {
        setMovableDirection(getMovableDirection(element));
        cache.set(key, element.scrollLeft);
      }
    },
    [key],
  );

  useLayoutEffect(() => {
    if (!slider.current) {
      return;
    }

    slider.current.scrollLeft = cache.get(key) || 0;
  }, [children, key]);

  useLayoutEffect(() => {
    handleMovableDirection(slider.current);
  }, [handleMovableDirection]);

  useEventListener('scroll', () => handleMovableDirection(slider.current), slider);
  useEventListener('resize', () => handleMovableDirection(slider.current));

  const getSlider = useCallback(() => {
    if (!slider.current) {
      throw new Error("🚫 Whoops! Can't find a slider element.");
    }

    return slider.current;
  }, []);

  const scrollBy = useCallback(
    (delta: number) => {
      scroll({
        target: getSlider(),
        by: delta * 10,
        duration: 750,
        easing: 'easeOutCubic',
      });
    },
    [getSlider],
  );

  const scrollTo = useCallback(
    (position: number) => {
      scroll({
        target: getSlider(),
        to: position,
        duration: 750,
        easing: 'easeOutCubic',
      });
    },
    [getSlider],
  );

  return (
    <SwimlaneContext.Provider value={{ movableDirection, slider, scrollBy, scrollTo }}>
      <section className={classes(css.container, className)}>{children}</section>
    </SwimlaneContext.Provider>
  );
}

function SwimlaneSlider({ children, className }: SwimlanePartProps) {
  const { slider, scrollBy } = useSwimlaneContext();

  return (
    <motion.div
      ref={slider}
      className={classes(css.slider, className)}
      onPan={(_, info) => {
        // Mobile devices scroll natively
        if (ScriptService.onMobile()) {
          return;
        }

        requestAnimationFrame(() => scrollBy(info.delta.x * 1.25));
      }}
      onPanStart={() => {
        // Mobile devices scroll natively
        if (ScriptService.onMobile()) {
          return;
        }

        set(slider.current, { pointerEvents: 'none' });
        set(slider.current?.parentElement, { cursor: 'grabbing' });
      }}
      onPanEnd={() => {
        requestAnimationFrame(() => {
          reset(slider.current);
          reset(slider.current?.parentElement);
        });
      }}
    >
      {children}
    </motion.div>
  );
}

interface SwimlaneButtonProps {
  direction: 'start' | 'end';
}

function SwimlaneButton({ direction }: SwimlaneButtonProps) {
  const { t } = useTranslation();
  const { movableDirection, scrollBy } = useSwimlaneContext();

  const isDisabled = (movableDirection === 'none' || direction !== movableDirection) && movableDirection !== 'both';

  if (movableDirection === 'none') {
    return null;
  }

  return (
    <button
      aria-label={direction === 'end' ? t('scroll right') : t('scroll left')}
      className={css.button}
      onClick={() => {
        const velocity = 100;
        const delta = direction === 'end' ? -velocity : velocity;
        requestAnimationFrame(() => scrollBy(delta));
      }}
      disabled={isDisabled}
    >
      {direction === 'end' ? <SvgChevronRight /> : <SvgChevronLeft />}
    </button>
  );
}

function SwimlaneButtons() {
  return (
    <Fragment>
      <SwimlaneButton direction="start" />
      <SwimlaneButton direction="end" />
    </Fragment>
  );
}

function SwimlaneHeader({ children, className }: SwimlanePartProps) {
  return <header className={classes(css.header, className)}>{children}</header>;
}

interface SwimlaneTitleProps extends SwimlanePartProps {
  to?: LinkProps['to'];
  href?: LinkProps['href'];
}

function SwimlaneTitle({ children, className, to, href }: SwimlaneTitleProps) {
  const classNames = classes(css.title, className);

  if (to !== undefined && href !== undefined) {
    warn('🚫 You can only use one of "to" or "href" props. "to" will be used.');
  }

  if (to !== undefined) {
    return (
      <h3 className={classNames}>
        <Link className={css.link} to={to}>
          <span>{children}</span>
          <SvgChevronRight className={css.icon} />
        </Link>
      </h3>
    );
  }

  if (href !== undefined) {
    return (
      <h3 className={classNames}>
        <a className={css.link} href={href}>
          <span>{children}</span>
          <SvgChevronRight className={css.icon} />
        </a>
      </h3>
    );
  }

  return <h3 className={classNames}>{children}</h3>;
}

Swimlane.Header = SwimlaneHeader;
Swimlane.Title = SwimlaneTitle;
Swimlane.Button = SwimlaneButton;
Swimlane.Buttons = SwimlaneButtons;
Swimlane.Slider = SwimlaneSlider;
